import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function GarbageSection({ samples, counts, onChange }) {
  const [emailRows, setEmailRows] = useState(null);
  const [testRows, setTestRows] = useState(null);
  const [confirmKind, setConfirmKind] = useState(null);
  const [busy, setBusy] = useState(false);

  async function loadEmailRows() {
    const { data } = await api.get(`/cleanup/garbage/email-rows`);
    setEmailRows(data.rows || []);
  }
  async function loadTestRows() {
    const { data } = await api.get(`/cleanup/garbage/test-brokerages`);
    setTestRows(data);
  }

  async function applyEmail() {
    setBusy(true);
    try {
      const { data } = await api.post(`/cleanup/garbage/email-rows/apply`, { confirm_token: "DELETE" });
      toast.success(`Deleted ${data.deleted} rows`);
      setConfirmKind(null); setEmailRows(null);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }
  async function applyTest() {
    setBusy(true);
    try {
      const { data } = await api.post(`/cleanup/garbage/test-brokerages/apply`, { confirm_token: "DELETE" });
      toast.success(`Deleted ${data.realtors_deleted} realtors + ${data.brokerages_deleted} brokerages`);
      setConfirmKind(null); setTestRows(null);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      {/* Subgroup A: Email {Name} rows */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-slate-900">{`"Email {Name}" parse artifacts`}</div>
            <div className="text-sm text-slate-500 mt-0.5">
              {counts.garbage_email_rows || 0} rows where the brokerage scraper grabbed an
              <code className="mx-1">{`<a href="mailto:...">Email Foo Bar</a>`}</code>
              link and treated it as a realtor.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadEmailRows} data-testid="garbage-email-preview">
            Preview rows
          </Button>
        </div>
        {emailRows !== null && (
          <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {emailRows.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No matching rows.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Brokerage</th>
                    <th className="text-left p-2">Phone</th>
                    <th className="text-left p-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {emailRows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="p-2">{r.name}</td>
                      <td className="p-2">{r.brokerage_name}</td>
                      <td className="p-2">{r.phone || "—"}</td>
                      <td className="p-2 text-slate-500">{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {emailRows.length > 0 && (
              <div className="p-3 flex justify-end border-t bg-slate-50">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmKind("email")}
                  data-testid="garbage-email-delete"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete {emailRows.length} rows
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Subgroup B: TEST brokerages */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-slate-900">"(TEST)" brokerage data</div>
            <div className="text-sm text-slate-500 mt-0.5">
              {counts.garbage_test_brokerage_rows || 0} realtors and {counts.garbage_test_brokerages || 0} brokerage(s) tagged "(TEST)".
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadTestRows} data-testid="garbage-test-preview">
            Preview rows
          </Button>
        </div>
        {testRows !== null && (
          <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {testRows.realtors?.length === 0 && testRows.brokerages?.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No matching rows.</div>
            ) : (
              <>
                {testRows.brokerages?.length > 0 && (
                  <div className="p-3 bg-amber-50 border-b text-xs">
                    Brokerages to delete: {testRows.brokerages.map((b) => b.name).join(", ")}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Realtor</th>
                      <th className="text-left p-2">Brokerage</th>
                      <th className="text-left p-2">Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testRows.realtors?.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="p-2">{r.name}</td>
                        <td className="p-2">{r.brokerage_name}</td>
                        <td className="p-2">{r.phone || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(testRows.realtors?.length > 0 || testRows.brokerages?.length > 0) && (
                  <div className="p-3 flex justify-end border-t bg-slate-50">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setConfirmKind("test")}
                      data-testid="garbage-test-delete"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete {testRows.realtors?.length || 0} realtors + {testRows.brokerages?.length || 0} brokerage(s)
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!confirmKind}
        onOpenChange={(open) => { if (!open) setConfirmKind(null); }}
      >
        <AlertDialogContent data-testid="garbage-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="garbage-confirm-title">
              {confirmKind === "email"
                ? `Delete ${emailRows?.length || 0} ${(emailRows?.length || 0) === 1 ? "record" : "records"}?`
                : `Delete ${(testRows?.realtors?.length || 0) + (testRows?.brokerages?.length || 0)} ${
                    (testRows?.realtors?.length || 0) + (testRows?.brokerages?.length || 0) === 1 ? "record" : "records"
                  }?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === "email" ? (
                <>
                  This will permanently delete <strong>{emailRows?.length || 0} "Email {`{Name}`}" rows</strong> that
                  were incorrectly imported from brokerage-site mailto link text. This cannot be undone.
                </>
              ) : (
                <>
                  This will permanently delete{" "}
                  <strong>
                    {testRows?.realtors?.length || 0} realtor record{(testRows?.realtors?.length || 0) === 1 ? "" : "s"}
                  </strong>
                  {(testRows?.brokerages?.length || 0) > 0 && (
                    <>
                      {" "}and the{" "}
                      <strong>
                        {testRows.brokerages.map((b) => `'${b.name}'`).join(", ")}{" "}
                        brokerage{testRows.brokerages.length === 1 ? "" : "s"}
                      </strong>
                    </>
                  )}
                  . This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="garbage-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => (confirmKind === "email" ? applyEmail() : applyTest())}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="garbage-confirm-apply"
            >
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
